// components/ExportTransferModal.jsx
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import TransferService from '../services/TransferService';
import UserService from '../services/UserService';
import AuthService from '../services/AuthService';

// ─── Hook ────────────────────────────────────────────────────────────────────

const useExportModal = (onHide) => {
    const APP_START_DATE = '2025-01-01';

    const [exportType,     setExportTypeState]     = useState('pdf');
    const [dateRangeType,  setDateRangeTypeState]  = useState('all');
    const [startDate,      setStartDate]           = useState('');
    const [endDate,        setEndDate]             = useState('');
    const [isLoading,      setIsLoading]           = useState(false);
    const [downloadReady,  setDownloadReady]       = useState(false);
    const [blobUrl,        setBlobUrl]             = useState(null);
    const [fileName,       setFileName]            = useState('');
    const [errors,         setErrors]              = useState({ exportType: '', startDate: '', endDate: '', user: '' });

    // ── Filtre utilisateur ────────────────────────────────────────────────────
    const [filterByUser,    setFilterByUser]    = useState(false);
    const [userSearch,      setUserSearch]      = useState('');
    const [allUsers,        setAllUsers]        = useState([]);
    const [userSuggestions, setUserSuggestions] = useState([]);
    const [selectedUser,    setSelectedUser]    = useState(null);
    const [loadingUsers,    setLoadingUsers]    = useState(false);

    const currentUser = AuthService.getCurrentUser();
    const isAdmin     = currentUser?.roleNames?.includes('ADMIN');

    // ── Chargement utilisateurs ───────────────────────────────────────────────
    useEffect(() => {
        if (filterByUser && isAdmin && allUsers.length === 0) {
            setLoadingUsers(true);
            UserService.getAllUsers(0, 200)
                .then(data => setAllUsers(data?.content || []))
                .catch(e => console.error('Erreur chargement users:', e))
                .finally(() => setLoadingUsers(false));
        }
    }, [filterByUser, isAdmin, allUsers.length]);

    // ── Suggestions ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!userSearch.trim()) { setUserSuggestions([]); return; }
        const q = userSearch.toLowerCase();
        setUserSuggestions(
            allUsers
                .filter(u =>
                    u.username?.toLowerCase().includes(q) ||
                    u.firstName?.toLowerCase().includes(q) ||
                    u.lastName?.toLowerCase().includes(q)
                )
                .slice(0, 8)
        );
    }, [userSearch, allUsers]);

    // ── Setters ───────────────────────────────────────────────────────────────
    const clearErrors = () => setErrors({ exportType: '', startDate: '', endDate: '', user: '' });

    const setExportType = (type) => {
        setExportTypeState(type);
        setDownloadReady(false);
        if (blobUrl) { URL.revokeObjectURL(blobUrl); setBlobUrl(null); }
        if (errors.exportType) setErrors(prev => ({ ...prev, exportType: '' }));
    };

    const setDateRangeType = (rangeType) => {
        setDateRangeTypeState(rangeType);
        setDownloadReady(false);
        if (blobUrl) { URL.revokeObjectURL(blobUrl); setBlobUrl(null); }
        setErrors(prev => ({ ...prev, startDate: '', endDate: '' }));
    };

    const handleToggleFilterByUser = (val) => {
        setFilterByUser(val);
        setSelectedUser(null);
        setUserSearch('');
        setUserSuggestions([]);
        setErrors(prev => ({ ...prev, user: '' }));
        setDownloadReady(false);
    };

    const handleSelectUser = (u) => {
        setSelectedUser(u);
        setUserSearch(`${u.firstName || ''} ${u.lastName || ''} (@${u.username})`);
        setUserSuggestions([]);
        setErrors(prev => ({ ...prev, user: '' }));
    };

    const handleClearUser = () => {
        setSelectedUser(null);
        setUserSearch('');
        setUserSuggestions([]);
    };

    // ── Validations ───────────────────────────────────────────────────────────
    const areDatesValid = useMemo(() => {
        if (dateRangeType === 'specific' && !startDate) return false;
        if (dateRangeType === 'range') {
            if (!startDate || !endDate) return false;
            if (new Date(startDate) > new Date(endDate)) return false;
        }
        return true;
    }, [dateRangeType, startDate, endDate]);

    const isUserValid = useMemo(() => {
        if (!filterByUser) return true;
        if (!isAdmin)      return true; // non-admin → son propre compte
        return !!selectedUser;
    }, [filterByUser, isAdmin, selectedUser]);

    const isExportDisabled = useMemo(
        () => isLoading || !exportType || !areDatesValid || !isUserValid,
        [isLoading, exportType, areDatesValid, isUserValid]
    );

    const infoMessage = useMemo(() => {
        if (!exportType)                                              return "Veuillez sélectionner un type d'export";
        if (filterByUser && isAdmin && !selectedUser)                return "Veuillez sélectionner un utilisateur";
        if (dateRangeType === 'range' && startDate && endDate
            && new Date(startDate) > new Date(endDate))              return "La date de fin doit être postérieure à la date de début";
        if ((dateRangeType === 'specific' && !startDate)
            || (dateRangeType === 'range' && (!startDate || !endDate))) return "Veuillez renseigner les dates requises";
        return null;
    }, [exportType, dateRangeType, startDate, endDate, filterByUser, isAdmin, selectedUser]);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const getTodayFormatted = () => new Date().toISOString().slice(0, 10);

    const extractFileName = (headers, fallback) => {
        try {
            const disposition = headers?.['content-disposition'];
            if (disposition) {
                let m = disposition.match(/filename\*=UTF-8''([^;\s]+)/);
                if (m?.[1]) return decodeURIComponent(m[1]);
                m = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (m?.[1]) return m[1].replace(/['"]/g, '').trim();
            }
        } catch (_) {}
        return fallback;
    };

    // ── Résolution des paramètres d'appel ─────────────────────────────────────
    const resolveParams = () => {
        const today    = getTodayFormatted();
        const start    = dateRangeType === 'all'      ? APP_START_DATE
            : dateRangeType === 'specific' ? startDate
                : startDate;
        const end      = dateRangeType === 'all'      ? today
            : dateRangeType === 'specific' ? startDate
                : endDate;
        const username = filterByUser
            ? (isAdmin ? selectedUser?.username : currentUser?.username)
            : null;
        return { start, end, username };
    };

    // ── Export ────────────────────────────────────────────────────────────────
    const handleExport = async () => {
        if (isExportDisabled) return;
        clearErrors();
        setIsLoading(true);
        setDownloadReady(false);

        try {
            const { start, end, username } = resolveParams();
            const ext          = exportType === 'excel' ? 'xlsx' : 'pdf';
            const defaultName  = username
                ? `Transferts_${username}_${start}_${end}.${ext}`
                : `Transferts_${start}_${end}.${ext}`;

            let response;
            if (exportType === 'excel') {
                response = username
                    ? await TransferService.exportExcelByCreatedBy(username, start, end)
                    : await TransferService.exportExcelByDateRange(start, end);
            } else {
                response = username
                    ? await TransferService.exportPdfByCreatedBy(username, start, end)
                    : await TransferService.exportPdfByDateRange(start, end);
            }

            const resolvedName = extractFileName(response.headers, defaultName);
            const mimeType     = resolvedName.endsWith('.xlsx')
                ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                : 'application/pdf';

            const blob = response.data instanceof Blob
                ? response.data
                : new Blob([response.data], { type: mimeType });

            const url = URL.createObjectURL(blob);
            setBlobUrl(url);
            setFileName(resolvedName);
            setDownloadReady(true);

        } catch (error) {
            console.error("Erreur export:", error);
            setErrors(prev => ({
                ...prev,
                exportType: "Une erreur est survenue lors de l'export. Veuillez réessayer."
            }));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = () => {
        if (!blobUrl || !fileName) return;
        const link = document.createElement('a');
        link.href  = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`✅ "${fileName}" exporté avec succès !`);
        resetAndClose();
    };

    const resetAndClose = () => {
        setExportTypeState('pdf');
        setDateRangeTypeState('all');
        setStartDate('');
        setEndDate('');
        setDownloadReady(false);
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
        setFileName('');
        setFilterByUser(false);
        setSelectedUser(null);
        setUserSearch('');
        setUserSuggestions([]);
        clearErrors();
        onHide();
    };

    return {
        exportType, dateRangeType, startDate, endDate,
        isLoading, downloadReady, fileName, errors,
        isExportDisabled, infoMessage,
        setExportType, setDateRangeType,
        setStartDate, setEndDate,
        handleExport, handleDownload, resetAndClose,
        // utilisateur
        filterByUser, isAdmin, currentUser,
        userSearch, setUserSearch,
        userSuggestions, selectedUser, loadingUsers,
        handleToggleFilterByUser, handleSelectUser, handleClearUser,
    };
};

// ─── Composant ───────────────────────────────────────────────────────────────

const ExportTransferModal = ({ show, onHide }) => {
    const suggestionsRef = useRef(null);

    const {
        exportType, dateRangeType, startDate, endDate,
        isLoading, downloadReady, fileName, errors,
        isExportDisabled, infoMessage,
        setExportType, setDateRangeType,
        setStartDate, setEndDate,
        handleExport, handleDownload, resetAndClose,
        filterByUser, isAdmin, currentUser,
        userSearch, setUserSearch,
        userSuggestions, selectedUser, loadingUsers,
        handleToggleFilterByUser, handleSelectUser, handleClearUser,
    } = useExportModal(onHide);

    const fieldsDisabled = isLoading || downloadReady;

    const DATE_RANGE_OPTIONS = [
        { key: 'all',      label: 'Tous les transferts'   },
        { key: 'specific', label: 'Date spécifique'       },
        { key: 'range',    label: 'Période personnalisée' },
    ];

    return (
        <Modal show={show} onHide={resetAndClose} centered size="lg">

            {/* ── Header ── */}
            <Modal.Header closeButton style={{ backgroundColor: '#0695b3', color: 'white' }}>
                <Modal.Title>
                    <i className="fas fa-download me-2" />
                    Exporter les transferts
                </Modal.Title>
            </Modal.Header>

            {/* ── Body ── */}
            <Modal.Body style={fieldsDisabled ? { opacity: 0.6, pointerEvents: 'none' } : {}}>

                {/* ── Type d'export ── */}
                <Form.Group className="mb-4">
                    <Form.Label>
                        <strong><i className="fas fa-file me-2 text-primary" />Type d'export</strong>
                    </Form.Label>
                    <div className="d-flex gap-3">
                        {[
                            { value: 'excel', label: 'Excel (.xlsx)', icon: 'fa-file-excel', color: '#217346' },
                            { value: 'pdf',   label: 'PDF (.pdf)',    icon: 'fa-file-pdf',   color: '#cc0000' },
                        ].map(({ value, label, icon, color }) => (
                            <div
                                key={value}
                                onClick={() => !fieldsDisabled && setExportType(value)}
                                style={{
                                    flex: 1, padding: '12px', borderRadius: '10px', textAlign: 'center',
                                    cursor: fieldsDisabled ? 'default' : 'pointer', transition: 'all .2s',
                                    border: `1.5px solid ${exportType === value ? '#0d6efd' : '#dee2e6'}`,
                                    backgroundColor: exportType === value ? 'rgba(13,110,253,0.08)' : '#fff',
                                }}
                            >
                                <i className={`fas ${icon} fa-lg me-2`} style={{ color }} />
                                <span style={{ color: exportType === value ? '#0d6efd' : '#000', fontWeight: exportType === value ? 600 : 400 }}>
                                    {label}
                                </span>
                            </div>
                        ))}
                    </div>
                    {errors.exportType && <div className="text-danger small mt-2">{errors.exportType}</div>}
                </Form.Group>

                {/* ── Filtre utilisateur ── */}
                <Form.Group className="mb-4">
                    <Form.Label>
                        <strong><i className="fas fa-user me-2 text-primary" />Filtrer par utilisateur</strong>
                    </Form.Label>

                    {/* Toggle */}
                    <div className="d-flex gap-2 mb-3">
                        {[
                            { val: false, label: 'Tous les utilisateurs' },
                            { val: true,  label: 'Un utilisateur précis'  },
                        ].map(({ val, label }) => (
                            <div
                                key={String(val)}
                                onClick={() => !fieldsDisabled && handleToggleFilterByUser(val)}
                                style={{
                                    flex: 1, padding: '10px 12px', borderRadius: '10px',
                                    cursor: fieldsDisabled ? 'default' : 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                    border: `1.5px solid ${filterByUser === val ? '#0d6efd' : '#dee2e6'}`,
                                    backgroundColor: filterByUser === val ? 'rgba(13,110,253,0.08)' : '#f8f9fa',
                                }}
                            >
                                <i
                                    className={`fas ${filterByUser === val ? 'fa-dot-circle' : 'fa-circle'}`}
                                    style={{ color: filterByUser === val ? '#0d6efd' : '#adb5bd', fontSize: '14px' }}
                                />
                                <span style={{ color: filterByUser === val ? '#0d6efd' : '#000', fontWeight: filterByUser === val ? 600 : 400, fontSize: '14px' }}>
                                    {label}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Recherche utilisateur (admin) */}
                    {filterByUser && isAdmin && (
                        <div className="position-relative mb-2" ref={suggestionsRef}>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="fas fa-search text-muted" />
                                </span>
                                <Form.Control
                                    type="text"
                                    placeholder="Rechercher un utilisateur..."
                                    value={userSearch}
                                    className="border-start-0"
                                    onChange={e => {
                                        setUserSearch(e.target.value);
                                        if (selectedUser) handleClearUser();
                                    }}
                                    disabled={fieldsDisabled}
                                />
                                {loadingUsers && (
                                    <span className="input-group-text bg-light">
                                        <Spinner animation="border" size="sm" variant="primary" />
                                    </span>
                                )}
                            </div>

                            {/* Suggestions */}
                            {userSuggestions.length > 0 && !selectedUser && (
                                <div style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050,
                                    backgroundColor: '#fff', border: '1px solid #dee2e6',
                                    borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                    maxHeight: '220px', overflowY: 'auto',
                                }}>
                                    {userSuggestions.map(u => (
                                        <div
                                            key={u.id || u.username}
                                            className="d-flex align-items-center gap-2 px-3 py-2"
                                            style={{
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #f3f4f6',
                                                transition: 'background .15s',
                                            }}
                                            onClick={() => handleSelectUser(u)}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <i className="fas fa-user-circle text-primary" />
                                            <div>
                                                <div className="fw-semibold small">{u.firstName} {u.lastName}</div>
                                                <div className="text-muted" style={{ fontSize: '11px' }}>@{u.username}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Utilisateur sélectionné (admin) */}
                    {filterByUser && isAdmin && selectedUser && (
                        <div className="d-flex align-items-center gap-2 px-3 py-2 rounded mb-1"
                             style={{ backgroundColor: '#e8f4ff', border: '1.5px solid #0d6efd' }}>
                            <i className="fas fa-check-circle text-primary" />
                            <span className="text-primary fw-semibold small flex-grow-1">
                                {selectedUser.firstName} {selectedUser.lastName} (@{selectedUser.username})
                            </span>
                            <button
                                className="btn btn-sm p-0 border-0 bg-transparent"
                                onClick={handleClearUser}
                                disabled={fieldsDisabled}
                            >
                                <i className="fas fa-times text-primary" />
                            </button>
                        </div>
                    )}

                    {/* Non-admin : son propre compte */}
                    {filterByUser && !isAdmin && (
                        <div className="d-flex align-items-center gap-2 px-3 py-2 rounded"
                             style={{ backgroundColor: '#e8f4ff', border: '1.5px solid #0d6efd' }}>
                            <i className="fas fa-user text-primary" />
                            <span className="text-primary fw-semibold small">@{currentUser?.username}</span>
                        </div>
                    )}

                    {errors.user && <div className="text-danger small mt-1">{errors.user}</div>}
                </Form.Group>

                {/* ── Période ── */}
                <Form.Group className="mb-3">
                    <Form.Label>
                        <strong><i className="fas fa-calendar me-2 text-primary" />Période</strong>
                    </Form.Label>
                    <div style={{ backgroundColor: '#f8f9fa', borderRadius: '12px', padding: '16px' }}>
                        {DATE_RANGE_OPTIONS.map(({ key, label }) => (
                            <div
                                key={key}
                                onClick={() => !fieldsDisabled && setDateRangeType(key)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '10px',
                                    padding: '10px 8px', borderRadius: '8px', marginBottom: '4px',
                                    cursor: fieldsDisabled ? 'default' : 'pointer',
                                    backgroundColor: dateRangeType === key ? 'rgba(13,110,253,0.1)' : 'transparent',
                                }}
                            >
                                <i
                                    className={`fas ${dateRangeType === key ? 'fa-dot-circle' : 'fa-circle'}`}
                                    style={{ color: dateRangeType === key ? '#0d6efd' : '#adb5bd', fontSize: '14px' }}
                                />
                                <span style={{ color: dateRangeType === key ? '#0d6efd' : '#000', fontWeight: dateRangeType === key ? 600 : 400 }}>
                                    {label}
                                </span>
                            </div>
                        ))}

                        {/* Sélecteurs de dates */}
                        {(dateRangeType === 'specific' || dateRangeType === 'range') && (
                            <Row className="mt-3 g-3">
                                <Col md={dateRangeType === 'range' ? 6 : 12}>
                                    <Form.Label className="text-primary small mb-1">
                                        {dateRangeType === 'specific' ? 'Date' : 'Date de début'}
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={startDate}
                                        max={new Date().toISOString().slice(0, 10)}
                                        onChange={e => setStartDate(e.target.value)}
                                        disabled={fieldsDisabled}
                                        isInvalid={!!errors.startDate}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.startDate}</Form.Control.Feedback>
                                </Col>
                                {dateRangeType === 'range' && (
                                    <Col md={6}>
                                        <Form.Label className="text-primary small mb-1">Date de fin</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={endDate}
                                            min={startDate}
                                            max={new Date().toISOString().slice(0, 10)}
                                            onChange={e => setEndDate(e.target.value)}
                                            disabled={fieldsDisabled}
                                            isInvalid={!!errors.endDate}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.endDate}</Form.Control.Feedback>
                                    </Col>
                                )}
                            </Row>
                        )}
                    </div>
                </Form.Group>

                {/* Message info */}
                {infoMessage && !isLoading && !downloadReady && (
                    <Alert variant="warning" className="d-flex align-items-center gap-2 py-2">
                        <i className="fas fa-exclamation-circle" />
                        <span className="small">{infoMessage}</span>
                    </Alert>
                )}
            </Modal.Body>

            {/* ── Footer ── */}
            <Modal.Footer>
                <Button variant="secondary" onClick={resetAndClose} disabled={isLoading}>
                    <i className="fas fa-times me-2" />Annuler
                </Button>

                {isLoading ? (
                    <Button variant="primary" disabled>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Export en cours...
                    </Button>
                ) : downloadReady ? (
                    <Button variant="success" onClick={handleDownload}>
                        <i className="fas fa-cloud-download-alt me-2" />
                        Télécharger — {fileName}
                    </Button>
                ) : (
                    <Button variant="primary" onClick={handleExport} disabled={isExportDisabled}>
                        <i className="fas fa-download me-2" />
                        Exporter
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ExportTransferModal;